import { Link, useLocation } from "react-router";
import {
  Award,
  BarChart3,
  Bell,
  Building2,
  ClipboardList,
  FileText,
  Filter,
  Handshake,
  Inbox,
  LayoutDashboard,
  Palette,
  Route,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Store,
  Target,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/shared/logo";

// Port of /components/admin/Sidebar.tsx. Same NAV/FOOTER lists, same
// permission gating, react-router Link/useLocation instead of next/link.
// NAV/FOOTER/visible are exported so the global-search palette can offer the
// same permission-filtered set of page-jump targets (single source of truth).
export type NavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  permission?: string;
};

export const NAV: NavItem[] = [
  { href: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/admin/partners", label: "Partners", icon: Building2, permission: "partners.read" },
  { href: "/admin/deals", label: "Deals", icon: Handshake, permission: "deals.read" },
  { href: "/admin/journeys", label: "Journeys", icon: Route, permission: "roles.manage" },
  { href: "/admin/tiers", label: "Tiers", icon: Award, permission: "tiers.read" },
  { href: "/admin/goals", label: "Goals", icon: Target, permission: "goals.read" },
  { href: "/admin/forms", label: "Forms", icon: ClipboardList, permission: "forms.read" },
  { href: "/admin/submissions", label: "Submissions", icon: Inbox, permission: "submissions.read" },
  { href: "/admin/pending-leads", label: "Queued leads", icon: Send, permission: "submissions.read" },
  { href: "/admin/listing-changes", label: "Listing changes", icon: Store, permission: "listings.read" },
  { href: "/admin/mdf", label: "Marketing Funds", icon: Wallet, permission: "mdf.read" },
  { href: "/admin/content", label: "Content", icon: FileText, permission: "content.read" },
  { href: "/admin/portal-content", label: "Portal content", icon: Palette, permission: "content.read" },
  { href: "/admin/analytics", label: "Analytics", icon: BarChart3, permission: "analytics.read" },
];

export const FOOTER: NavItem[] = [
  { href: "/admin/settings", label: "Settings", icon: Settings, permission: "settings.read" },
  { href: "/admin/users", label: "Users", icon: Users, permission: "users.read" },
  { href: "/admin/settings/roles", label: "Roles", icon: ShieldCheck, permission: "roles.read" },
  { href: "/admin/settings/segments", label: "Segments", icon: Filter, permission: "settings.read" },
  {
    href: "/admin/settings/notifications",
    label: "Notifications",
    icon: Bell,
    permission: "notifications.read",
  },
];

export function visible(items: NavItem[], permissions: string[]): NavItem[] {
  return items.filter((i) => !i.permission || permissions.includes(i.permission));
}

export function AdminSidebar({
  permissions,
  onOpenSearch,
}: {
  permissions: string[];
  onOpenSearch: () => void;
}) {
  const { pathname } = useLocation();
  const nav = visible(NAV, permissions);
  const footer = visible(FOOTER, permissions);

  return (
    <aside className="sticky top-0 hidden h-screen w-[240px] shrink-0 flex-col border-r bg-card/30 px-3 py-5 md:flex">
      <Link to="/admin/dashboard" className="mb-6 px-2">
        <Logo />
      </Link>

      <button
        type="button"
        onClick={onOpenSearch}
        className="group mb-4 inline-flex w-full items-center gap-2.5 rounded-md border border-border/60 bg-background/50 px-2.5 py-1.5 text-sm text-muted-foreground transition-colors hover:border-border hover:bg-muted hover:text-foreground"
      >
        <Search className="h-4 w-4 text-muted-foreground/80 transition-transform group-hover:scale-105" />
        <span>Search</span>
        <kbd className="ml-auto rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </button>

      <nav className="flex flex-1 flex-col gap-0.5">
        {nav.map((item) => {
          const active = pathname === item.href || pathname.startsWith(item.href + "/");
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "group inline-flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors",
                active
                  ? "bg-accent/10 text-accent"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <item.icon
                className={cn(
                  "h-4 w-4 transition-transform group-hover:scale-105",
                  active ? "text-accent" : "text-muted-foreground/80",
                )}
              />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="mt-4 border-t pt-4">
        {footer.map((item) => {
          const active =
            item.href === "/admin/settings"
              ? pathname === item.href
              : pathname === item.href || pathname.startsWith(item.href + "/");
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "inline-flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                active
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <item.icon className="h-4 w-4" />
              <span>{item.label}</span>
            </Link>
          );
        })}
        <p className="mt-4 px-2.5 text-[10px] uppercase tracking-wider text-muted-foreground/70">
          Colab v0.1 · Bookboost
        </p>
      </div>
    </aside>
  );
}
